<template>
  <v-card class="rounded-lg mb-10 pa-4" flat color="#2EAEFF1A">
    <v-row no-gutters align="center">
      <v-icon color="#2E46ff" class="mr-2">
        {{ numberOfBookingsNotExpired > 0 ? 'mdi-bell-badge-outline' : 'mdi-bell-outline' }}
      </v-icon>
      <span v-if="numberOfBookingsNotExpired > 0"> {{ numberOfBookingsNotExpired }} bookings within the next week </span>
      <span v-else> No bookings within the next week </span>
      <v-spacer />
      <ElevatedBtn @click="showBooking = true">Book charger</ElevatedBtn>
    </v-row>
    <!-- booking dialog -->
    <GenericDialog :activator="showBooking" @close="showBooking = false">
      <ChargerBookingContent :charger="charger" />
    </GenericDialog>
  </v-card>
</template>

<script setup lang="ts">
import {
  type DirectusAssetBooking,
  fetchAssetBookingsByAssetID,
} from '@/api/calls/directus-calls/asset-bookings-calls'
import type Charger from '@/classes/charger_classes/charger'
import { DateTime, Interval } from 'luxon'
import { computed, onMounted, ref, watch } from 'vue'
import GenericDialog from '../dialog-wrappers/GenericDialog.vue'
import ElevatedBtn from '../ui-elements/buttons/ElevatedBtn.vue'
import ChargerBookingContent from './ChargerBookingContent.vue'

const { charger } = defineProps<{ charger: Charger }>()

const bookings = ref<DirectusAssetBooking[]>([])
const showBooking = ref(false)
const today = DateTime.now()
const weekInterval = Interval.fromDateTimes(today.startOf('day'), today.startOf('day').plus({ days: 6 }))
const numberOfBookingsNotExpired = computed(() => {
  return bookings.value.reduce((count, booking) => {
    // guard vs no booking dates - NOTE: this should never happen without data being corrupted.
    if (!booking.start || !booking.end) return count

    // check for case where edge case booking ends after the start of the week.
    if (weekInterval.contains(DateTime.fromISO(booking.end))) {
      return count + 1
    }

    // check for case where edge case booking starts before the end of the week.
    if (weekInterval.contains(DateTime.fromISO(booking.start))) {
      return count + 1
    }

    // check for case where booking encompasses the whole week
    if (weekInterval.isAfter( DateTime.fromISO(booking.start)) && weekInterval.isBefore(DateTime.fromISO(booking.end))) {
      return count + 1
    }
    
    return count
  }, 0)
})

async function fetchBookingsData() {
  const directusFormatEvents = await fetchAssetBookingsByAssetID(charger.evses.map((evse) => evse.uid).filter(Boolean))
  if (directusFormatEvents) bookings.value = directusFormatEvents
}

onMounted(() => {
  fetchBookingsData()
})

watch(
  () => charger,
  () => {
    fetchBookingsData()
  },
)
watch(showBooking, (value) => {
  if (!value) fetchBookingsData() // update booking numbers when dialog is closed
})
</script>
